import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle, Button } from '@bookaburra/ui';
import { Bot, Mic, Phone, PhoneOff, Sparkles, User as UserIcon } from 'lucide-react';
import { fetchBookingTenant } from '../booking-flow/api';
import { useDocumentTitle } from '../lib/use-document-title';
import { AppHeader } from '../components/AppHeader';

export const Route = createFileRoute('/voice/$slug')({
  component: VoicePage,
});

interface ChatChoice {
  label: string;
  spokenAs?: string;
}

interface Turn {
  id: number;
  role: 'assistant' | 'user';
  content: string;
  /** For assistant turns: which intent router stage matched (debug aid). */
  source?: string;
  /** For user turns: true while Flux is still emitting partial transcripts.
   *  Replaced by the final transcript when EndOfTurn arrives. */
  partial?: boolean;
}

type CallStatus = 'idle' | 'connecting' | 'live' | 'ended' | 'error';
type AgentState = 'listening' | 'thinking' | 'speaking';
type EndReason = 'idle' | 'goodbye' | 'other';

/**
 * Voice booking assistant — full-duplex over WebSocket.
 *
 * The browser captures continuous 16-bit-LE PCM from the microphone
 * via an AudioWorklet, streams it to the VoiceAgent Durable Object,
 * which forwards each frame to Deepgram Flux for streaming STT with
 * server-side voice-activity detection. The agent responds with
 * Deepgram Aura-2 TTS audio over the same socket.
 *
 * Wire protocol matches apps/web/src/voice/voice-agent.ts.
 */
function VoicePage() {
  const { slug } = Route.useParams();
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `Call ${tenantQ.data.tenant.name}` : 'Voice assistant',
  );

  const [status, setStatus] = useState<CallStatus>('idle');
  const [agentState, setAgentState] = useState<AgentState>('listening');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [choices, setChoices] = useState<ChatChoice[]>([]);
  const [cta, setCta] = useState<{ label: string; href: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [endReason, setEndReason] = useState<EndReason | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  /** Tracks any in-flight playback so hangup can stop the speaker. */
  const playbackRef = useRef<AudioBufferSourceNode | null>(null);

  useEffect(() => {
    return () => {
      hangup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function dial() {
    setStatus('connecting');
    setError(null);
    setEndReason(null);
    setTurns([]);
    setChoices([]);
    setCta(null);

    try {
      // Pre-flight eligibility check.
      const statusRes = await fetch(
        `/api/voice/${encodeURIComponent(slug)}/status`,
      );
      const statusBody = (await statusRes.json().catch(() => null)) as {
        enabled?: boolean;
        reason?: string;
        detail?: string;
        used?: number;
        cap?: number;
      } | null;
      if (!statusBody?.enabled) {
        const detail =
          statusBody?.reason === 'budget_exhausted'
            ? `Monthly voice limit reached for this business (${statusBody.used}/${statusBody.cap} minutes). Try chat or the booking page.`
            : statusBody?.detail ?? 'Voice agent is not available for this business.';
        setError(detail);
        setStatus('error');
        return;
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      mediaStreamRef.current = mediaStream;

      // Sample rate hint: ask the AudioContext for 16 kHz so we don't
      // have to resample. Browsers may ignore the hint and give us
      // device-native (often 48 kHz); whatever we get, we tell the
      // server the actual rate and Flux handles it.
      const audioCtx = new AudioContext({ sampleRate: 16000 });
      audioCtxRef.current = audioCtx;
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume();
      }
      await audioCtx.audioWorklet.addModule('/audio/pcm-recorder-worklet.js');

      const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(
        `${scheme}//${window.location.host}/api/voice/${encodeURIComponent(slug)}/socket`,
      );
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.addEventListener('open', () => {
        setStatus('live');
        // Tell the server what rate the PCM frames will arrive at so
        // it can declare the same rate upstream to Flux.
        ws.send(
          JSON.stringify({
            type: 'hello',
            sampleRate: audioCtx.sampleRate,
          }),
        );

        const source = audioCtx.createMediaStreamSource(mediaStream);
        const node = new AudioWorkletNode(audioCtx, 'pcm-recorder');
        node.port.onmessage = (event) => {
          if (ws.readyState !== WebSocket.OPEN) return;
          ws.send(event.data as ArrayBuffer);
        };
        // We connect the worklet but don't pipe it through to the
        // speaker — the caller hearing themselves would be confusing
        // and would feed back into Flux.
        source.connect(node);
        sourceNodeRef.current = source;
        workletNodeRef.current = node;
      });

      ws.addEventListener('message', (event) => {
        if (typeof event.data === 'string') {
          handleControlMessage(event.data);
        } else {
          void playAudio(event.data as ArrayBuffer);
        }
      });
      ws.addEventListener('close', (event) => {
        // Server-side reasons we surface in the UI. 'idle' is the
        // auto-hangup after 30s + 30s of silence; 'goodbye' is the
        // caller saying bye or tapping End call. Anything else
        // (network drops, server errors) falls through as 'other'.
        const reason: EndReason =
          event.reason === 'idle'
            ? 'idle'
            : event.reason === 'goodbye'
              ? 'goodbye'
              : 'other';
        setEndReason(reason);
        setStatus((s) => (s === 'live' || s === 'connecting' ? 'ended' : s));
        cleanupMedia();
      });
      ws.addEventListener('error', () => {
        setError('Connection error. Try again.');
        setStatus('error');
        cleanupMedia();
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? `Couldn't start call: ${err.message}`
          : "Couldn't start call",
      );
      setStatus('error');
      cleanupMedia();
    }
  }

  function hangup() {
    try {
      wsRef.current?.close();
    } catch {
      // ignore
    }
    wsRef.current = null;
    cleanupMedia();
    setStatus((s) => (s === 'live' ? 'ended' : s));
  }

  function cleanupMedia() {
    try {
      workletNodeRef.current?.disconnect();
    } catch {
      // ignore
    }
    try {
      sourceNodeRef.current?.disconnect();
    } catch {
      // ignore
    }
    workletNodeRef.current = null;
    sourceNodeRef.current = null;
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    try {
      playbackRef.current?.stop();
    } catch {
      // already stopped
    }
    playbackRef.current = null;
    try {
      void audioCtxRef.current?.close();
    } catch {
      // ignore
    }
    audioCtxRef.current = null;
  }

  function tapChoice(index: number) {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const choice = choices[index];
    if (!choice) return;
    setTurns((prev) => [
      ...prev,
      { id: Date.now(), role: 'user', content: choice.spokenAs ?? choice.label },
    ]);
    ws.send(JSON.stringify({ type: 'tap', index }));
  }

  function handleControlMessage(raw: string) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    const msg = parsed as {
      type?: string;
      text?: string;
      partial?: boolean;
      reply?: string;
      choices?: ChatChoice[];
      cta?: { label: string; href: string };
      source?: string;
      state?: AgentState;
    };
    if (msg.type === 'state' && msg.state) {
      setAgentState(msg.state);
      return;
    }
    if (msg.type === 'transcript' && msg.text) {
      // Partial transcripts replace the previous partial in-place;
      // final transcripts append as a stable turn.
      setTurns((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'user' && last.partial) {
          const next = prev.slice(0, -1);
          return [
            ...next,
            {
              id: last.id,
              role: 'user',
              content: msg.text ?? '',
              partial: msg.partial === true,
            },
          ];
        }
        return [
          ...prev,
          {
            id: Date.now(),
            role: 'user',
            content: msg.text ?? '',
            partial: msg.partial === true,
          },
        ];
      });
      return;
    }
    if (msg.type === 'reply' && msg.reply) {
      setTurns((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: msg.reply ?? '',
          source: msg.source,
        },
      ]);
      setChoices(msg.choices ?? []);
      setCta(msg.cta ?? null);
      return;
    }
  }

  async function playAudio(buffer: ArrayBuffer) {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    try {
      const decoded = await ctx.decodeAudioData(buffer.slice(0));
      // Stop any in-flight playback so back-to-back replies don't
      // overlap if the server speaks twice quickly.
      try {
        playbackRef.current?.stop();
      } catch {
        // ignore — already stopped
      }
      const src = ctx.createBufferSource();
      src.buffer = decoded;
      src.connect(ctx.destination);
      src.onended = () => {
        if (playbackRef.current === src) playbackRef.current = null;
      };
      src.start();
      playbackRef.current = src;
    } catch (err) {
      console.warn('[voice] audio decode failed', err);
    }
  }

  const live = status === 'live';

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader
        tenant={
          tenantQ.data
            ? {
                slug: tenantQ.data.tenant.slug,
                name: tenantQ.data.tenant.name,
                timezone: tenantQ.data.tenant.timezone,
              }
            : undefined
        }
      />
      <main className="mx-auto flex max-w-2xl flex-col px-6 py-8 sm:px-12">
        <header className="mb-6">
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Voice assistant
          </p>
          <h1 className="mt-2 text-2xl font-bold tracking-tight">
            {tenantQ.data ? `Call ${tenantQ.data.tenant.name}` : 'Voice assistant'}
          </h1>
          <p className="text-sm text-muted-foreground">
            Tap Start call and just talk — the assistant will reply when you
            pause. It will pre-fill the booking form for you. Your booking
            isn't confirmed until you open that page and complete it.
          </p>
        </header>

        <div className="mb-6 flex flex-wrap items-center gap-3">
          {status === 'live' ? (
            <Button
              onClick={hangup}
              variant="destructive"
              size="lg"
              className="whitespace-nowrap"
              leadingIcon={<PhoneOff className="h-4 w-4" />}
            >
              End call
            </Button>
          ) : (
            // leadingIcon (not a child) keeps the SVG as a flex sibling
            // of the text — Tailwind preflight forces `svg { display:
            // block; }`, so dropping the icon into children stacks it
            // on top of the label.
            <Button
              onClick={dial}
              size="lg"
              disabled={status === 'connecting'}
              className="whitespace-nowrap"
              leadingIcon={<Phone className="h-4 w-4" />}
            >
              {status === 'connecting' ? 'Connecting…' : 'Start call'}
            </Button>
          )}
          {live ? (
            <span
              aria-live="polite"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground"
            >
              <span
                className={`h-2 w-2 rounded-full ${
                  agentState === 'listening'
                    ? 'animate-pulse bg-emerald-500'
                    : agentState === 'speaking'
                      ? 'bg-primary'
                      : 'bg-amber-500'
                }`}
              />
              {agentState === 'listening' && 'Listening…'}
              {agentState === 'thinking' && 'Thinking…'}
              {agentState === 'speaking' && 'Speaking…'}
            </span>
          ) : null}
        </div>

        {error ? (
          <Alert variant="destructive" className="mb-6">
            <AlertTitle>Voice trouble</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {status === 'ended' && endReason === 'idle' ? (
          <Alert className="mb-6">
            <AlertTitle>Call ended due to inactivity</AlertTitle>
            <AlertDescription>
              We didn't hear from you for a while, so the call ended. Tap
              Start call to try again.
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="flex-1 space-y-4 overflow-y-auto rounded-2xl border border-brand-sage/15 bg-card p-4 shadow-brand sm:max-h-[60vh] sm:p-6">
          {turns.length === 0 && status === 'idle' ? (
            <p className="flex gap-3 text-sm text-muted-foreground">
              <Bot className="mt-0.5 h-4 w-4" />
              Press <span className="font-semibold">Start call</span> to begin.
            </p>
          ) : null}
          {turns.map((t) => (
            <div
              key={t.id}
              className={`flex gap-3 ${t.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              <div
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                  t.role === 'user'
                    ? 'bg-secondary text-foreground'
                    : 'bg-primary text-primary-foreground'
                }`}
              >
                {t.role === 'user' ? (
                  <UserIcon className="h-4 w-4" />
                ) : (
                  <Bot className="h-4 w-4" />
                )}
              </div>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
                  t.role === 'user' ? 'bg-secondary' : 'bg-primary/10'
                } ${t.partial ? 'opacity-60 italic' : ''}`}
              >
                <p className="whitespace-pre-wrap">{t.content}</p>
              </div>
            </div>
          ))}
        </div>

        {live && choices.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {choices.map((c, i) => (
              <Button key={i} variant="outline" size="sm" onClick={() => tapChoice(i)}>
                {c.label}
              </Button>
            ))}
          </div>
        ) : null}

        {cta ? (
          <div className="mt-4">
            <a
              href={cta.href}
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              {cta.label}
            </a>
          </div>
        ) : null}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          The assistant only pre-fills the booking form — your booking is
          confirmed once you complete it on the booking page. If speech
          recognition gets stuck, tap a button instead.
        </p>
      </main>
    </div>
  );
}
